import { personName } from '../../personName';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { motion } from 'framer-motion';

import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';

import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';

import AvatarWithStatus from './AvatarWithStatus';
import { isDirectChat } from './directChat';
import { LayoutContext } from '../../context';
import {
  CHAT, MOTION, ellipsis, listTime, attachmentsLabel,
} from './chatTheme';

/**
 * Карточка чата в списке.
 *
 * Что изменилось по сравнению с прежней версией:
 *  • строка стала компактной и фиксированной высоты — раньше карточка росла
 *    от количества чипов с вложениями, и список «дышал» при прокрутке;
 *  • у всех контейнеров проставлен minWidth: 0. Без этого flex-элемент не
 *    может стать уже своего содержимого, и длинный адрес вроде
 *    «Событие #25705 · Новый Уренгой, улица 26-го съезда КПСС, 9А» распирал
 *    колонку — из-за этого весь список чатов и был такой широкий;
 *  • время переехало вправо вверх, счётчик непрочитанных — вправо вниз,
 *    как в привычных мессенджерах;
 *  • разделительные линии убраны, выбранный чат — залитая плашка.
 */
export default function ChatListItem({
  chat, onSelect, selected, index = 0, onContextMenu, presence = null,
}) {
  const navigate = useNavigate();
  const { unreadCounts, getChatUnread } = useContext(LayoutContext);

  // Долгое нажатие = правая кнопка. На телефоне контекстного меню нет,
  // а закрепить чат и разложить по папкам нужно и там.
  const longPressRef = useRef(null);
  const movedRef = useRef(false);

  useEffect(() => () => {
    if (longPressRef.current) clearTimeout(longPressRef.current);
  }, []);

  const openMenu = (e) => {
    if (!onContextMenu) return;
    e.preventDefault();
    e.stopPropagation();
    const point = e.touches?.[0] || e.changedTouches?.[0] || e;
    onContextMenu({ x: point.clientX, y: point.clientY }, chat);
  };

  // Счётчик берём из самого чата, если сервер его прислал (мгновенно, без
  // второго запроса), иначе — из общей карты счётчиков.
  const countUnread = unreadCounts?.timestamp
    ? (getChatUnread?.(chat?.chat_id) || 0)
    : (Number(chat?.unread_count) || 0);


  const last = chat?.last_message;
  const isSystem = last?.type_sender === 'system';
  const senderName = personName(last?.sender, '');
  const attachLabel = attachmentsLabel(last?.attachments);
  const preview = (last?.content || '').trim() || attachLabel;
  const time = listTime(last?.date_create || chat?.date_create);

  // Статусы в списке: в диалоге точка про собеседника, в группе — счётчик
  // «сколько сейчас в сети». Пока сводка не приехала, не показываем ничего:
  // серая точка у всех подряд читается как «все вышли», а это неправда.
  const isDm = isDirectChat(chat);
  const onlineCount = Number(presence?.online || 0);

  return (
    <Box
      component={motion.div}
      layout="position"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: MOTION.base, delay: Math.min(index, 12) * 0.018, ease: MOTION.ease }}
      onClick={() => {
        onSelect(chat);
        navigate(`/chats?chatId=${chat.chat_id}`, { replace: true });
      }}
      onContextMenu={openMenu}
      onTouchStart={(e) => {
        movedRef.current = false;
        const pt = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        if (longPressRef.current) clearTimeout(longPressRef.current);
        longPressRef.current = setTimeout(() => {
          if (!movedRef.current) onContextMenu?.(pt, chat);
        }, 500);
      }}
      onTouchMove={() => {
        // Палец поехал — это прокрутка списка, а не долгое нажатие
        movedRef.current = true;
        if (longPressRef.current) { clearTimeout(longPressRef.current); longPressRef.current = null; }
      }}
      onTouchEnd={() => {
        if (longPressRef.current) { clearTimeout(longPressRef.current); longPressRef.current = null; }
      }}
      sx={(theme) => ({
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        minWidth: 0,
        px: 1,
        py: 1,
        mx: 0.75,
        my: 0.25,
        borderRadius: '14px',
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'background-color .16s ease, transform .16s ease',
        backgroundColor: selected
          ? (theme.palette.mode === 'dark' ? 'rgba(99,102,241,0.22)' : 'rgba(99,102,241,0.12)')
          : chat?.is_pinned
            ? (theme.palette.mode === 'dark' ? 'rgba(99,102,241,0.10)' : 'rgba(99,102,241,0.045)')
            : 'transparent',
        '&:hover': {
          backgroundColor: selected
            ? (theme.palette.mode === 'dark' ? 'rgba(99,102,241,0.26)' : 'rgba(99,102,241,0.16)')
            : chat?.is_pinned
              ? (theme.palette.mode === 'dark' ? 'rgba(99,102,241,0.16)' : 'rgba(99,102,241,0.08)')
              : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(22,34,71,0.04)'),
        },
        '&:active': { transform: 'scale(0.985)' },
      })}
    >
      {/* Полоска слева у активного чата */}
      {selected && (
        <Box
          component={motion.span}
          layoutId="chat-list-active"
          transition={MOTION.softSpring}
          sx={{
            position: 'absolute', left: 0, top: 10, bottom: 10, width: 3,
            borderRadius: 3, backgroundColor: 'primary.500',
          }}
        />
      )}

      <Box sx={{ position: 'relative', flexShrink: 0, display: 'inline-flex' }}>
        <AvatarWithStatus
          name={chat.display_name}
          size="md"
          showStatus={isDm && !!presence}
          online={!!presence?.peer_online}
        />
        {!isDm && onlineCount > 0 && (
          // Группа: зелёный счётчик на углу аватара. Точка тут врала бы —
          // «кто-то из двадцати» и «девятнадцать из двадцати» выглядели бы
          // одинаково.
          <Box
            component={motion.span}
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={MOTION.spring}
            sx={{
              position: 'absolute', right: -3, bottom: -3,
              minWidth: 16, height: 16, px: 0.4,
              borderRadius: '8px',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9.5, fontWeight: 800, lineHeight: 1, color: '#fff',
              backgroundColor: '#22c55e',
              border: '2px solid var(--joy-palette-background-surface, #fff)',
              boxShadow: '0 1px 4px rgba(34,197,94,0.45)',
            }}
          >
            {onlineCount > 9 ? '9+' : onlineCount}
          </Box>
        )}
      </Box>

      {/* Центральная колонка. minWidth:0 обязателен — иначе эллипсис не работает */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ minWidth: 0 }}>
          <Typography
            level="title-sm"
            sx={{
              ...ellipsis,
              flex: 1,
              fontWeight: countUnread ? 800 : 650,
              color: 'text.primary',
            }}
          >
            {chat.display_name}
          </Typography>
          {isDm && <Typography level="body-xs" sx={{ flexShrink: 0, fontSize: 9.5, fontWeight: 800,
            color: 'primary.600', bgcolor: 'primary.softBg', px: 0.5, borderRadius: '5px' }}>ЛС</Typography>}
          {chat?.is_pinned && (
            <PushPinRoundedIcon
              sx={{
                fontSize: 13, flexShrink: 0, color: 'text.tertiary',
                transform: 'rotate(35deg)',
              }}
            />
          )}
          {!!time && (
            <Typography
              level="body-xs"
              sx={{
                flexShrink: 0,
                fontSize: 11,
                color: countUnread ? 'primary.500' : 'text.tertiary',
                fontWeight: countUnread ? 700 : 400,
              }}
            >
              {time}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, mt: 0.25 }}>
          {isSystem && (
            <CampaignRoundedIcon sx={{ fontSize: 14, color: 'text.tertiary', flexShrink: 0 }} />
          )}
          <Typography
            level="body-xs"
            sx={{
              ...ellipsis,
              flex: 1,
              fontSize: 12.5,
              lineHeight: 1.35,
              color: countUnread ? 'text.secondary' : 'text.tertiary',
              fontWeight: countUnread ? 500 : 400,
            }}
          >
            {senderName && !isSystem && (
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {senderName}:{' '}
              </Box>
            )}
            {preview || 'Нет сообщений'}
          </Typography>

          {/* Счётчик непрочитанных — пружинит при появлении */}
          {countUnread > 0 ? (
            <Box
              component={motion.span}
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={MOTION.spring}
              sx={{
                flexShrink: 0,
                minWidth: 20, height: 20, px: 0.75,
                borderRadius: 10,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: 'primary.500',
                color: '#fff',
                fontSize: 11, fontWeight: 800, lineHeight: 1,
                boxShadow: '0 2px 6px rgba(99,102,241,0.4)',
              }}
            >
              {countUnread > 99 ? '99+' : countUnread}
            </Box>
          ) : (
            last && (
              <DoneAllRoundedIcon
                sx={{ fontSize: 15, color: 'text.tertiary', opacity: 0.55, flexShrink: 0 }}
              />
            )
          )}
        </Stack>
      </Box>
    </Box>
  );
}
